import { Alert, Badge, BarChart, Button, buttonClass, cardClass, fieldClass, selectClass, Table } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Loading, outcomeMessage, outcomeTone, Time, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { formatCell } from '../format';

// Dashboards: a layout of reports from the registry. A draft is yours alone;
// sharing is a signed version. Every widget names the report engine and version
// behind its numbers. Patterns: data-display/dashboard, data-display/statistics,
// and data-display/table for each widget.

interface DashRow { id: string; name: string; description: string | null; status: string; version: number; owner_name: string; mine: boolean; widgets: number; drafted_by: string; updated_at: string }
interface ReportDef { id: string; version: string; title: string; description: string; params: { properties?: Record<string, any> } }
// A widget whose report is no longer registered comes back as { title, error } only.
type Widget =
  | { title: string; viz: string; width: number; engine: { id: string; version: string }; columns: Array<{ key: string; label: string; kind: string }>; rows: Array<Record<string, any>>; error?: undefined }
  | { title: string; error: string };
interface Rendered { id: string; name: string; description: string | null; status: string; version: number; owner_user_id: string; widgets: Widget[]; rendered_at: string }

export function Dashboards() {
  const list = useApi<DashRow[]>('/v1/dashboards');
  usePageTitle('Dashboards');
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Dashboards</h1><p>Built only from registered reports. Your drafts are yours alone; a shared dashboard is a signed version.</p></div>
        <Link className={buttonClass()} to="/dashboards/new"><Icon name="plus" />New dashboard</Link>
      </div>
      <ErrorBanner error={list.error} />
      <section className={cardClass}>
        {list.loading && !list.data ? <ListSkeleton rows={3} label="Loading dashboards" /> : !list.data?.length ? <Empty title="No dashboards yet" action={<Link className={buttonClass({ size: 'sm' })} to="/dashboards/new">New dashboard</Link>}>Pick reports such as pipeline by stage or campaign results.</Empty> : (
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {list.data.map((d) => (
              <li key={d.id}><Link className="list-item" to={`/dashboards/${d.id}`}>
                <div className="grow"><div className="item-title">{d.name}</div><div className="item-sub">{d.widgets} widgets · {d.mine ? 'yours' : `by ${d.owner_name}`}{d.drafted_by.startsWith('agent:') ? ' · drafted by an agent' : ''}</div></div>
                <Badge tone={d.status === 'signed' ? 'success' : 'neutral'}>{d.status === 'signed' ? `Shared, v${d.version}` : 'Draft, only you'}</Badge>
                <span className="faint"><Time iso={d.updated_at} /></span>
              </Link></li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function DashboardNew() {
  const reports = useApi<ReportDef[]>('/v1/reports');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<Record<string, { viz: string; since?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  usePageTitle('New dashboard');
  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const widgets = Object.entries(picked).map(([report, w]) => ({ report, viz: w.viz, width: 2, ...(w.since ? { params: { since_days: Number(w.since) } } : {}) }));
      const out = await api<{ id: string }>('/v1/dashboards', { body: { name, definition: { layout: 'grid', widgets } } });
      navigate(`/dashboards/${out.id}`);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/dashboards', label: 'Dashboards' }, { label: 'New' }]} />
      <div className="page-head"><div><h1>New dashboard</h1><p>Choose reports from the registry. It saves as your draft.</p></div></div>
      <form className={`${cardClass} card-pad stack`} onSubmit={save}>
        <ErrorBanner error={error} focusOnShow />
        <Field label="Name" required><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <fieldset className="field"><legend>Reports</legend>
          {!reports.data ? <Loading label="Loading the report registry" /> : (
            <ul className="stack" style={{ gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
              {reports.data.map((r) => {
                const on = !!picked[r.id];
                return (
                  <li key={r.id} className="row" style={{ alignItems: 'flex-start' }}>
                    <input type="checkbox" id={`r-${r.id}`} checked={on} onChange={(e) => setPicked((p) => { const n = { ...p }; if (e.target.checked) n[r.id] = { viz: 'table' }; else delete n[r.id]; return n; })} style={{ marginTop: 4 }} />
                    <label htmlFor={`r-${r.id}`} className="grow"><strong>{r.title}</strong> <code className="engine">{r.id} {r.version}</code><div className="small muted">{r.description}</div></label>
                    {on && <>
                      <label className="sr-only" htmlFor={`v-${r.id}`}>Show {r.title} as</label>
                      <select id={`v-${r.id}`} className={`${selectClass} h-9 w-auto`} style={{ width: 'auto' }} value={picked[r.id]!.viz} onChange={(e) => setPicked({ ...picked, [r.id]: { ...picked[r.id]!, viz: e.target.value } })}><option value="table">Table</option><option value="bar">Bars</option><option value="number">Total</option></select>
                      {r.params.properties?.since_days && <><label className="sr-only" htmlFor={`s-${r.id}`}>Window for {r.title}</label>
                        <select id={`s-${r.id}`} className={`${selectClass} h-9 w-auto`} style={{ width: 'auto' }} value={picked[r.id]!.since ?? ''} onChange={(e) => setPicked({ ...picked, [r.id]: { ...picked[r.id]!, since: e.target.value } })}><option value="">Default window</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option><option value="365">A year</option></select></>}
                    </>}
                  </li>
                );
              })}
            </ul>
          )}
        </fieldset>
        <div><Button type="submit" disabled={busy || !name || !Object.keys(picked).length}>{busy ? 'Saving…' : 'Save draft'}</Button></div>
      </form>
    </div>
  );
}

export function DashboardView() {
  const { id = '' } = useParams();
  const { me, refreshStats } = useSession();
  const dash = useApi<Rendered>(`/v1/dashboards/${id}`, [id]);
  const versions = useApi<Array<{ version: number; signed_by_name: string; signed_at: string }>>(`/v1/dashboards/${id}/versions`, [id]);
  const [dialog, setDialog] = useState<'share' | 'archive' | null>(null);
  const [share, setShare] = useState<{ workspace: boolean; roles: string[] }>({ workspace: true, roles: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const d = dash.data;
  usePageTitle(d?.name ?? 'Dashboard');
  const mine = d?.owner_user_id === me.user_id;

  async function publish() {
    setBusy(true); setError(null);
    try {
      const out = await api<Outcome>(`/v1/dashboards/${id}/publish`, { body: { shared_with: { workspace: share.workspace, roles: share.workspace ? [] : share.roles, user_ids: [] }, reason: 'Share this dashboard' } });
      toast(outcomeMessage(out, 'Shared'), outcomeTone(out)); setDialog(null); refreshStats(); void dash.reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function archive() {
    setBusy(true); setError(null);
    try { const out = await api<any>(`/v1/dashboards/${id}/archive`, { body: {} }); toast(out.state ? outcomeMessage(out, 'Archived') : 'Archived'); navigate('/dashboards'); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function duplicate() {
    try { const out = await api<{ id: string }>(`/v1/dashboards/${id}/duplicate`, { body: {} }); toast('Copied to a new draft'); navigate(`/dashboards/${out.id}`); } catch (e) { setError(e); }
  }

  if (dash.loading && !d) return <Loading label="Running the reports" />;
  if (!d) return <div className="stack"><ErrorBanner error={dash.error} /><Link to="/dashboards">All dashboards</Link></div>;
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/dashboards', label: 'Dashboards' }, { label: d.name }]} />
      <div className="page-head">
        <div><div className="row"><h1>{d.name}</h1><Badge tone={d.status === 'signed' ? 'success' : 'neutral'}>{d.status === 'signed' ? `Shared, version ${d.version}` : 'Draft, only you'}</Badge></div><p>Run <Time iso={d.rendered_at} />. Each widget shows the report engine and version behind its numbers.</p></div>
        <div className="row">
          {mine && <Button type="submit" onClick={() => setDialog('share')}>Share…</Button>}
          <Button type="submit" variant="secondary" onClick={duplicate}>Duplicate</Button>
          {mine && <Button type="submit" variant="ghost" onClick={() => setDialog('archive')}>Archive…</Button>}
        </div>
      </div>
      <ErrorBanner error={dialog ? null : error} />
      <div className="widgets">
        {d.widgets.map((w, i) => <WidgetView key={i} w={w} />)}
      </div>
      {!!versions.data?.length && <p className="faint">Signed versions: {versions.data.map((v) => `v${v.version} by ${v.signed_by_name}`).join(', ')}</p>}
      <ConfirmDialog open={dialog === 'share'} title={`Share ${d.name}?`} confirmLabel="Propose sharing" busy={busy} onConfirm={publish} onCancel={() => setDialog(null)}>
        <p style={{ margin: 0 }}>Sharing is a signed version: a manager or admin other than you signs it. Until then it stays your draft.</p>
        <ErrorBanner error={error} />
        <label className="row small"><input type="checkbox" checked={share.workspace} onChange={(e) => setShare({ ...share, workspace: e.target.checked })} />Everyone in the workspace</label>
        {!share.workspace && <fieldset className="field"><legend>Roles that can see it</legend><div className="row">{['admin', 'manager', 'member'].map((r) => <label key={r} className="row small"><input type="checkbox" checked={share.roles.includes(r)} onChange={(e) => setShare({ ...share, roles: e.target.checked ? [...share.roles, r] : share.roles.filter((x) => x !== r) })} />{r}</label>)}</div></fieldset>}
      </ConfirmDialog>
      <ConfirmDialog open={dialog === 'archive'} title={`Archive ${d.name}?`} confirmLabel="Archive" tone="danger" busy={busy} onConfirm={archive} onCancel={() => setDialog(null)}>
        <p style={{ margin: 0 }}>{d.status === 'draft' ? 'Your draft is archived and kept.' : 'A shared dashboard is archived through a change set someone else signs.'}</p>
        <ErrorBanner error={error} />
      </ConfirmDialog>
    </div>
  );
}

function WidgetView({ w }: { w: Widget }) {
  // Inline error state: the widget keeps its place and title, says what failed, and the rest of the dashboard still renders.
  if ('error' in w && w.error !== undefined) return (
    <section className={`${cardClass} widget w2`} aria-label={w.title}>
      <div className="card-head"><h2>{w.title}</h2></div>
      <div className="card-pad"><Alert tone="danger" role="note" title="This widget could not run.">{w.error}</Alert></div>
    </section>
  );
  if (!('rows' in w)) return null;
  const currency = (w.rows[0]?.currency as string) ?? 'USD';
  // A report that carries currency gives a row per currency; money is totalled per currency, never across.
  const currencies = [...new Set(w.rows.map((r) => r.currency as string | undefined).filter(Boolean))] as string[];
  const mixed = currencies.length > 1;
  const numeric = w.columns.find((c) => c.kind !== 'text');
  const label = w.columns.find((c) => c.kind === 'text');
  const engine = <div><code className="engine">{w.engine ? `${w.engine.id} ${w.engine.version}` : ''}</code></div>;
  const total = numeric ? w.rows.reduce((n, r) => n + Number(r[numeric.key] ?? 0), 0) : 0;
  return (
    <section className={`${cardClass} widget w${w.viz === 'number' ? 1 : Math.min(Math.max(w.width ?? 2, 1), 4)}`} aria-label={w.title}>
      <div className="card-head"><h2>{w.title}</h2>{engine}</div>
      {!w.rows.length ? <Empty title="No data in this window" /> : w.viz === 'number' && numeric ? (
        <div className="card-pad">{(mixed && numeric.kind === 'money' ? currencies : [null]).map((c) => (
          <div key={c ?? 'all'} className="stat" style={{ padding: 0 }}>
            <div className="num">{formatCell(numeric.kind, c ? w.rows.filter((r) => r.currency === c).reduce((n, r) => n + Number(r[numeric.key] ?? 0), 0) : total, c ?? currency)}</div>
            <div className="label">{numeric.label}, total{c ? ` in ${c}` : ''}</div>
          </div>
        ))}</div>
      ) : w.viz === 'bar' && numeric && label ? (
        <div className="card-pad">
          <BarChart label={`${w.title}: ${numeric.label} by ${label.label}`}
            data={w.rows.map((r) => ({ label: `${String(r[label.key])}${mixed ? ` (${r.currency})` : ''}`, value: Number(r[numeric.key] ?? 0) / (numeric.kind === 'money' ? 10_000 : 1), text: String(formatCell(numeric.kind, r[numeric.key], (r.currency as string) ?? currency)) }))} />
        </div>
      ) : (
        <Table label={w.title}>
          <caption className="sr-only">{w.title}</caption>
          <thead><tr>{w.columns.map((c) => <th key={c.key} scope="col">{c.label}</th>)}</tr></thead>
          <tbody>{w.rows.map((r, i) => <tr key={i}>{w.columns.map((c) => <td key={c.key}>{formatCell(c.kind, r[c.key], (r.currency as string) ?? currency)}</td>)}</tr>)}</tbody>
        </Table>
      )}
    </section>
  );
}
